/**
 * GuardedRoute Component
 * 
 * Protects routes based on authentication status and role permissions.
 * 
 * Role Hierarchy (from lowest to highest privileges):
 * - 'user': Base level access
 * - 'support': Mid-level access, includes all 'user' permissions
 * - 'admin': Highest level access, includes all 'support' and 'user' permissions
 * 
 * When 'requiredRole' is specified:
 * - If user has no role, access is denied
 * - If user's role is equal to or higher than the required role in hierarchy, access is granted
 *   (e.g., an 'admin' can access routes requiring 'support' or 'user' roles)
 * - If a role is not in the known hierarchy, an exact match is required
 */
import BoardsLoader from '@common-components/tasks-panel/recents-boards/boards-loader';
import { User } from '@common-models/user';
import { Stack } from '@mui/material';
import { selectUser } from '@common-reducers/UserSelectors';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { namespace } from '@common/config/constants';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

export type RedirectCallback = (user: User) => string;

// Define role hierarchy - higher index means higher privilege
const RoleHierarchy = ['user', 'support', 'admin'];

// Function to check if user role has required permission
const hasRequiredRole = (userRole?: string, requiredRole?: string): boolean => {
  console.log('[route-guard] hasRequiredRole check:', { userRole, requiredRole });
  
  if (!requiredRole) {
    console.log('[route-guard] No required role specified, access granted');
    return true; // No role required
  }
  
  if (!userRole) {
    console.log('[route-guard] User has no role, access denied');
    return false; // User has no role
  }
  
  const userRoleIndex = RoleHierarchy.indexOf(userRole);
  const requiredRoleIndex = RoleHierarchy.indexOf(requiredRole);
  
  console.log('[route-guard] Role indexes:', { 
    userRoleIndex, 
    requiredRoleIndex,
    roleHierarchy: RoleHierarchy 
  });
  
  // If role is not in hierarchy, exact match is required
  if (userRoleIndex === -1 || requiredRoleIndex === -1) {
    const exactMatch = userRole === requiredRole;
    console.log('[route-guard] Role not in hierarchy, using exact match:', exactMatch);
    return exactMatch;
  }
  
  // Check if user role is high enough in hierarchy
  // Higher index means higher privilege, so we check if userRoleIndex >= requiredRoleIndex
  const hasAccess = userRoleIndex >= requiredRoleIndex;
  console.log('[route-guard] Checking role hierarchy access:', hasAccess);
  return hasAccess;
};

export interface GuardedRouteProps {
    element: JSX.Element;
    auth?: boolean;
    redirect?: string | RedirectCallback;
    redirectBack?: boolean;
    requiredRole?: string;
}

export default function GuardedRoute({
    element,
    auth,
    redirect,
    requiredRole,   
    redirectBack = true
}: GuardedRouteProps) {
    // Please do not delete this line as it enables the page to refresh without any exceptions from translation.
    useTranslation(namespace);
    const authenticating = useAppSelector((state) => state.UserReducer.authenticating);
    const user = useAppSelector(selectUser);
    const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
    const currentRoute = window.location.pathname
    const finalRedirect = useMemo(() => (searchParams.get('redirect') ?? redirect) as string, [redirect, searchParams]);

    // Check for redirect loop
    const redirectCounter = useMemo(() => {
        const key = `route-guard-redirect-${currentRoute}`;
        const count = parseInt(sessionStorage.getItem(key) || '0');
        
        // Only update on initial render
        if (count < 5) {
            sessionStorage.setItem(key, (count + 1).toString());
        }
        
        return count;
    }, [currentRoute]);

    // Log user and role information for debugging
    console.log('[route-guard] GuardedRoute:', {
        path: currentRoute,
        user: user ? { id: user._id, email: user.email, role: user.role } : 'No user',
        requiredRole,
        auth,
        redirectCounter,
        hasRequiredPermission: user ? hasRequiredRole(user.role, requiredRole) : false,
        condition: {
            authAndNoUser: auth && !user,
            noAuthAndUser: auth === false && user,
            roleCheckFailed: requiredRole && !hasRequiredRole(user?.role, requiredRole)
        }
    });

    if (authenticating && currentRoute !== '/login' && currentRoute !== '/signup') {
        console.log('[route-guard] User is authenticating, showing loader');
        return (
            <Stack sx={{ width: '100vw', height: '100vh' }} justifyContent="center" alignItems="center">
                <BoardsLoader />
            </Stack>
        );
    }

    // More explicit check for authorization and role requirements
    const isAuthenticated = !!user;
    const isAuthRequired = !!auth;
    const hasRole = isAuthenticated && hasRequiredRole(user?.role, requiredRole);
    const accessDenied = (isAuthRequired && !isAuthenticated) || 
                         (auth === false && isAuthenticated) || 
                         (requiredRole && !hasRole);

    // Handle the redirection only after authentication completes
    if (!authenticating && accessDenied) {
        // Force break redirects if we detect too many attempts on the same route
        if (redirectCounter >= 3) {
            console.log('[route-guard] Too many redirects detected for this route, rendering fallback content');
            // For role-based permissions, just show an error element
            if (requiredRole && !hasRole) {
                return (
                    <Stack sx={{ width: '100vw', height: '100vh', p: 4 }} justifyContent="center" alignItems="center">
                        <h1>Access Denied</h1>
                        <p>You don't have the required permissions to access this page.</p>
                        <p>Required role: {requiredRole}</p>
                    </Stack>
                );
            }
            // For auth issues, return to a safe page directly
            window.location.href = isAuthenticated ? '/tasks' : '/login';
            return null;
        }

        // Determine the exact reason for denial
        let reason = "";
        if (isAuthRequired && !isAuthenticated) {
            reason = "Not authenticated";
        } else if (auth === false && isAuthenticated) {
            reason = "Already authenticated";
        } else if (requiredRole && !hasRole) {
            reason = `Insufficient role permissions - Required: '${requiredRole}', User has: '${user?.role || "no role"}'`;
        }
        
        console.log('[route-guard] Access denied, redirecting', {
            isAuthenticated,
            isAuthRequired,
            hasRole,
            redirect,
            finalRedirect,
            reason
        });
        
        // Default redirect path for role-based access issues
        let _redirect = '/login';
        
        // If not authenticated, use login page
        if (isAuthRequired && !isAuthenticated) {
            _redirect = '/login';
        } 
        // If already authenticated but accessing a non-auth page
        else if (auth === false && isAuthenticated) {
            _redirect = '/tasks';
        } 
        // For role-based access issues, use login (or custom redirect)
        else if (requiredRole && !hasRole) {
            _redirect = redirect ? (typeof redirect === 'string' ? redirect : redirect(new User(user))) : '/login';
        }
        
        // Prevent redirect loops by checking current path
        if (_redirect === window.location.pathname) {
            // Break the loop - redirect to a safe path
            console.log('[route-guard] Detected potential redirect loop, redirecting to safe path');
            if (isAuthenticated) {
                _redirect = '/tasks'; // Safe default for authenticated users
            } else {
                _redirect = '/login'; // Safe default for unauthenticated users
            }
        }
        
        // Build the URL for redirection
        let search = '';
        if (redirectBack && _redirect === '/login') {
            searchParams.set('redirect', window.location.pathname);
            search = `?${searchParams.toString()}`;
        }
        
        // Apply function-based redirect if provided - already handled above
        
        const url = new URL(_redirect + search, window.location.origin);
        console.log('[route-guard] Final redirect URL', url.toString(), 'Current path:', window.location.pathname);

        return window.location.pathname !== _redirect ? (
            <Navigate
                to={{
                    pathname: url.pathname,
                    search: url.search,
                }}
                replace={true}
            />
        ) : element;
    }

    console.log('[route-guard] Access granted for path', currentRoute);
    return element;
}
